import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, uploadUrl } from '../hooks/useApi';
import Icon from '../components/Icon';
import './Catalogue.css';

const CATEGORIES = [
  { value: '', label: 'Toutes' },
  { value: 'viande', label: 'Viandes' },
  { value: 'sauce', label: 'Sauces' },
  { value: 'side', label: 'Sides' },
  { value: 'boisson', label: 'Boissons' },
  { value: 'extra', label: 'Extras' },
  { value: 'forfait', label: 'Forfaits' },
];

function badgeWC(produit) {
  if (!produit.wc_sku)
    return <span className="badge">Non WC</span>;
  if (!produit.wc_product_id)
    return <span className="badge badge--info">Brouillon</span>;
  if (produit.wc_statut === 'publish')
    return <span className="badge badge--success">Publié</span>;
  return <span className="badge badge--info">{produit.wc_statut || 'Draft'}</span>;
}

function badgeStock(produit) {
  if (!produit.wc_sku) return null;
  const stock = produit.stock_reel ?? 0;
  if (stock === 0)
    return <span className="badge badge--danger">Rupture</span>;
  return <span className="badge badge--gold">Stock : {stock}</span>;
}

/* Squelette de chargement */
function SkeletonTable() {
  return (
    <div className="cat-skeleton">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="cat-skel-row">
          <div className="skeleton cat-skel-thumb" />
          <div className="cat-skel-info">
            <div className="skeleton cat-skel-name" />
            <div className="skeleton cat-skel-sub" />
          </div>
          <div className="skeleton cat-skel-badge" />
          <div className="skeleton cat-skel-badge" />
          <div className="skeleton cat-skel-badge" />
          <div className="skeleton cat-skel-actions" />
        </div>
      ))}
    </div>
  );
}

export default function Catalogue() {
  const navigate = useNavigate();
  const [produits,  setProduits]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [erreur,    setErreur]    = useState('');
  const [categorie, setCategorie] = useState('');
  const [actifOnly, setActifOnly] = useState(true);
  const [recherche, setRecherche] = useState('');
  const [syncing,   setSyncing]   = useState(false);
  const [msg,       setMsg]       = useState('');

  const charger = useCallback(async () => {
    try {
      setLoading(true);
      setErreur('');
      const params = new URLSearchParams();
      if (categorie) params.set('categorie', categorie);
      if (actifOnly)  params.set('actif', '1');
      if (recherche)  params.set('q', recherche);
      const data = await api.get(`/catalogue?${params}`);
      setProduits(Array.isArray(data) ? data : []);
    } catch (e) {
      setErreur(e.message);
    } finally {
      setLoading(false);
    }
  }, [categorie, actifOnly, recherche]);

  useEffect(() => { charger(); }, [charger]);

  function showMsg(m) { setMsg(m); setTimeout(() => setMsg(''), 4000); }

  async function toggleActif(produit) {
    try {
      await api.patch(`/catalogue/${produit.id}/toggle`);
      charger();
    } catch (e) { alert('Erreur : ' + e.message); }
  }

  async function supprimer(produit) {
    if (!window.confirm(`Supprimer "${produit.nom}" définitivement ?`)) return;
    try {
      await api.delete(`/catalogue/${produit.id}`);
      charger();
    } catch (e) { alert('Erreur : ' + e.message); }
  }

  async function syncWC() {
    if (!window.confirm('Pousser tous les produits actifs vers WooCommerce ?')) return;
    try {
      setSyncing(true);
      const res = await api.post('/catalogue/push-wc-batch');
      showMsg(`Synchronisation : ${res.succes || 0} OK, ${res.erreurs || 0} erreur(s)`);
      charger();
    } catch (e) { alert('Erreur sync WC : ' + e.message); }
    finally { setSyncing(false); }
  }

  const photoProduit = (p) => {
    if (p.photo_principale_url) {
      const url = p.photo_principale_url.startsWith('http')
        ? p.photo_principale_url
        : uploadUrl(p.photo_principale_url);
      return <img src={url} alt={p.nom} className="cat-thumb-img" />;
    }
    return (
      <div className="cat-thumb-fallback">
        <Icon name="package" size={22} />
      </div>
    );
  };

  return (
    <div className="catalogue-page">

      {msg && (
        <div className="cat-flash-ok rise-in">
          <Icon name="succes" size={16} />
          {msg}
        </div>
      )}

      {/* ── En-tête ── */}
      <div className="page-header">
        <div className="page-header-text">
          <h1 className="page-title">Catalogue Produits</h1>
          <p className="page-subtitle muted">Gestion centralisée des produits Escamel</p>
        </div>
        <div className="cat-header-actions">
          <button className="btn btn-ghost" onClick={syncWC} disabled={syncing}>
            <Icon name="rafraichir" size={15} />
            {syncing ? 'Sync…' : 'Sync WooCommerce'}
          </button>
          <button className="btn btn-primary" onClick={() => navigate('/catalogue/new')}>
            <Icon name="ajouter" size={15} />
            Nouveau produit
          </button>
        </div>
      </div>

      {/* ── Filtres ── */}
      <div className="cat-filters card rise-in">
        <div className="cat-search-wrap">
          <Icon name="recherche" size={15} color="var(--muted)" />
          <input
            type="text"
            className="cat-search-input"
            placeholder="Rechercher un produit…"
            value={recherche}
            onChange={e => setRecherche(e.target.value)}
          />
        </div>
        <div className="cat-categs">
          {CATEGORIES.map(c => (
            <button
              key={c.value}
              className={`btn btn-ghost btn--sm cat-categ-btn${categorie === c.value ? ' is-active' : ''}`}
              onClick={() => setCategorie(c.value)}
            >
              {c.label}
            </button>
          ))}
        </div>
        <label className="cat-actif-toggle">
          <input type="checkbox" checked={actifOnly} onChange={e => setActifOnly(e.target.checked)} />
          Actifs seulement
        </label>
      </div>

      {/* ── États ── */}
      {loading && <SkeletonTable />}

      {!loading && erreur && (
        <div className="error-inline rise-in">
          <Icon name="erreur" size={16} />
          {erreur}
          <button className="btn btn-ghost btn--sm" style={{ marginLeft: 'auto' }} onClick={charger}>Réessayer</button>
        </div>
      )}

      {!loading && !erreur && produits.length === 0 && (
        <div className="card">
          <div className="empty-state">
            <Icon name="package" size={40} />
            <p className="empty-state__titre">Aucun produit trouvé</p>
            <p>Modifiez les filtres ou créez un premier produit.</p>
          </div>
        </div>
      )}

      {/* ── Tableau ── */}
      {!loading && !erreur && produits.length > 0 && (
        <div className="card cat-table-card rise-in" style={{ overflow: 'auto' }}>
          <table className="esc-table catalogue-table">
            <thead>
              <tr>
                <th style={{ width: 56 }}>Photo</th>
                <th>Produit</th>
                <th>Catégorie</th>
                <th>Prix</th>
                <th>Stock réel</th>
                <th>WooCommerce</th>
                <th style={{ width: 100 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {produits.map(p => (
                <tr key={p.id} className={p.actif ? '' : 'cat-row-inactif'}>
                  <td>{photoProduit(p)}</td>
                  <td>
                    <div className="cat-nom">{p.nom}</div>
                    {p.wc_sku && <div className="cat-sku muted">{p.wc_sku}</div>}
                    {!p.actif && <span className="badge" style={{ fontSize: 10, marginTop: 3 }}>Inactif</span>}
                  </td>
                  <td>
                    <span className="cat-categorie">
                      {p.categorie}
                      {p.sous_categorie && <span className="muted"> / {p.sous_categorie}</span>}
                    </span>
                  </td>
                  <td>
                    {p.prix_base > 0
                      ? <span className="cat-prix">{Number(p.prix_base).toFixed(2)} $</span>
                      : <span className="muted">—</span>
                    }
                    {p.unite_prix && <span className="muted" style={{ fontSize: 11 }}> /{p.unite_prix}</span>}
                  </td>
                  <td>{badgeStock(p)}</td>
                  <td>{badgeWC(p)}</td>
                  <td>
                    <div className="td-actions">
                      <button
                        className="btn btn-ghost btn--sm"
                        title="Modifier"
                        onClick={() => navigate(`/catalogue/${p.id}`)}
                      >
                        <Icon name="modifier" size={14} />
                      </button>
                      <button
                        className={`btn btn--sm ${p.actif ? 'btn-ghost' : 'btn-primary'}`}
                        title={p.actif ? 'Désactiver' : 'Activer'}
                        onClick={() => toggleActif(p)}
                      >
                        <Icon name={p.actif ? 'arreter' : 'demarrer'} size={14} />
                      </button>
                      <button
                        className="btn btn-danger btn--sm"
                        title="Supprimer"
                        onClick={() => supprimer(p)}
                      >
                        <Icon name="supprimer" size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
